// frontend/src/components/Landing/Landing.js

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import './Landing.css';

const CONTACTO_VENTAS = 'contacto@doublecoding.com';

const BENEFICIOS = [
  {
    icono: '🎯',
    titulo: 'Decisiones objetivas',
    texto: 'Basa tus contrataciones en datos medibles, no solo en la impresión de una entrevista.'
  },
  {
    icono: '⚡',
    titulo: 'Resultados inmediatos',
    texto: 'Tus candidatos resuelven en línea y tú recibes los resultados apenas terminan.'
  },
  {
    icono: '📊',
    titulo: 'Reportes profesionales',
    texto: 'Un PDF claro y listo para compartir con tu equipo de RH o con dirección.'
  },
  {
    icono: '🧩',
    titulo: 'Pruebas a tu medida',
    texto: 'Combina las evaluaciones que necesitas o pide un stack diseñado para tu vacante.'
  }
];

const STACKS = [
  { icono: '🧠', nombre: 'Prueba de Razonamiento', texto: 'Inteligencia general y capacidad de razonamiento para perfiles administrativos.' },
  { icono: '🔎', nombre: 'Prueba de Razonamiento ágil', texto: 'Prueba de inteligencia ágil, ideal para procesos de alto volumen.' },
  { icono: '⚙️', nombre: 'Prueba de conducta', texto: 'Estilo de comportamiento, liderazgo y trabajo en equipo.' },
  { icono: '🏭', nombre: 'PERFOP', texto: 'Perfil de personalidad operativa para puestos operativos e industriales.' },
  { icono: '🧩', nombre: 'Crea pruebas personalizadas', texto: 'Diseña tus propios cuestionarios y combínalos con las pruebas oficiales en tu stack.' }
];

const PASOS = [
  { numero: '1', titulo: 'Elige tu stack', texto: 'Selecciona una combinación ya lista o solicita una diseñada a tu medida.' },
  { numero: '2', titulo: 'Invita a tus candidatos', texto: 'Cada candidato recibe un enlace único por correo, sin instalar nada.' },
  { numero: '3', titulo: 'Resuelven en línea', texto: 'A su propio ritmo, desde cualquier dispositivo, con progreso guardado.' },
  { numero: '4', titulo: 'Recibe tus resultados', texto: 'Reportes y hallazgos listos para apoyar tu decisión de contratación.' }
];

const Landing = () => {
  const navigate = useNavigate();
  const [autenticado, setAutenticado] = useState(false);

  useEffect(() => {
    let activo = true;
    fetch('/api/auth/verificar', { credentials: 'include' })
      .then(res => res.json())
      .then(data => { if (activo) setAutenticado(!!data.autenticado); })
      .catch(() => {});
    return () => { activo = false; };
  }, []);

  const irAlAcceso = () => navigate(autenticado ? '/panel' : '/login');

  const scrollA = (id) => (e) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="landing">
      <header className="landing-nav">
        <div className="landing-nav-inner">
          <img src="/Logotransparente.png" alt="PsyTest" className="landing-logo" />
          <nav className="landing-nav-links">
            <a href="#stacks" onClick={scrollA('stacks')}>Stacks</a>
            <a href="#como-funciona" onClick={scrollA('como-funciona')}>Cómo funciona</a>
            <a href="#contacto" onClick={scrollA('contacto')}>Contacto</a>
          </nav>
          <div className="landing-nav-actions">
            <button className="btn-nav-login" onClick={irAlAcceso}>
              {autenticado ? 'Ir a mi panel' : 'Iniciar sesión'}
            </button>
          </div>
        </div>
      </header>

      <section className="landing-hero">
        <div className="landing-hero-shapes" aria-hidden="true">
          <span className="landing-hero-shape landing-hero-shape-1" />
          <span className="landing-hero-shape landing-hero-shape-2" />
          <span className="landing-hero-shape landing-hero-shape-3" />
        </div>

        <div className="landing-hero-inner">
          <div className="landing-hero-content">
            <span className="landing-eyebrow">Evaluación psicométrica para empresas</span>
            <h1>
              Contrata con evidencia,{' '}
              <span className="landing-hero-acento">no solo con intuición</span>
            </h1>
            <p className="landing-hero-sub">
              PsyTest te ayuda a evaluar el potencial de tus candidatos con pruebas psicométricas
              en línea: elige entre nuestros stacks ya validados o pide uno diseñado exactamente
              para el perfil que buscas.
            </p>
            <div className="landing-hero-actions">
              {!autenticado && (
                <button className="btn-hero-primary" onClick={() => navigate('/registro')}>
                  Registra tu empresa gratis
                </button>
              )}
              <button className="btn-hero-secondary" onClick={() => navigate('/solicitar-demo')}>
                Solicitar demostración
              </button>
            </div>
            <div className="landing-trust-row">
              <span><CheckCircle2 size={18} aria-hidden="true" /> 100% en línea</span>
              <span><CheckCircle2 size={18} aria-hidden="true" /> Resultados en minutos</span>
              <span><CheckCircle2 size={18} aria-hidden="true" /> Reportes en PDF</span>
            </div>
          </div>

          <div className="landing-hero-visual">
            <img
              src="/PanelSuperiorPcyMovil.png"
              alt="Panel de PsyTest en computadora y celular: dashboard de candidatos, perfil psicométrico y pantalla de instrucciones de la prueba"
              width="1448"
              height="1086"
              fetchpriority="high"
            />
          </div>
        </div>
      </section>

      <section className="landing-section">
        <div className="landing-section-inner">
          <h2 className="landing-section-title">Por qué elegir PsyTest</h2>
          <div className="landing-grid landing-grid-4">
            {BENEFICIOS.map((b) => (
              <div className="landing-card" key={b.titulo}>
                <div className="landing-card-icon">{b.icono}</div>
                <h3>{b.titulo}</h3>
                <p>{b.texto}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="stacks" className="landing-section landing-section-alt">
        <div className="landing-section-inner">
          <h2 className="landing-section-title">Nuestros stacks</h2>
          <p className="landing-section-subtitle">
            Combínalos según el puesto que quieras cubrir, o pídenos un stack diseñado desde cero.
          </p>
          <div className="landing-grid landing-grid-5">
            {STACKS.map((s) => (
              <div className="landing-card landing-card-stack" key={s.nombre}>
                <div className="landing-card-icon">{s.icono}</div>
                <h3>{s.nombre}</h3>
                <p>{s.texto}</p>
              </div>
            ))}
          </div>
          <div className="landing-a-medida">
            <div>
              <h3>¿Buscas algo distinto?</h3>
              <p>Diseñamos e integramos pruebas a la medida de tu proceso de selección.</p>
            </div>
            <button className="btn-cta-primary" onClick={() => navigate('/solicitar-demo')}>Platícanos tu caso</button>
          </div>
        </div>
      </section>

      <section id="como-funciona" className="landing-section">
        <div className="landing-section-inner">
          <h2 className="landing-section-title">Cómo funciona</h2>
          <div className="landing-steps">
            {PASOS.map((p) => (
              <div className="landing-step" key={p.numero}>
                <div className="landing-step-numero">{p.numero}</div>
                <h3>{p.titulo}</h3>
                <p>{p.texto}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="contacto" className="landing-contact">
        <div className="landing-section-inner landing-contact-inner">
          <h2>¿Listo para mejorar tu proceso de selección?</h2>
          <p>Escríbenos y te ayudamos a elegir el stack ideal para tu empresa.</p>
          <a className="btn-cta-light" href={`mailto:${CONTACTO_VENTAS}`}>{CONTACTO_VENTAS}</a>
        </div>
      </section>

      <footer className="landing-footer">
        <img src="/isotipotransparente.png" alt="" className="landing-footer-icon" aria-hidden="true" />
        <p>© {new Date().getFullYear()} PsyTest · Sistema de Pruebas Psicométricas</p>
        <button className="landing-footer-login" onClick={irAlAcceso}>
          {autenticado ? 'Ir a mi panel' : 'Acceso empleados'}
        </button>
      </footer>
    </div>
  );
};

export default Landing;
